import React, { useState, useEffect, useRef } from 'react';

// Format price based on value: 6 significant digits for $10 and under, 2 decimal places for over $10
const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '0.00';
  if (price > 10) {
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    return price.toLocaleString('en-US', { maximumSignificantDigits: 6 });
  }
};

function TradeHistory({ coinId, onLastPriceUpdate }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const activeCoinRef = useRef(coinId);

  useEffect(() => {
    activeCoinRef.current = coinId;
    if (coinId) {
      fetchTrades(coinId);
      const interval = setInterval(() => fetchTrades(coinId), 1000);
      return () => clearInterval(interval);
    }
  }, [coinId]);

  const fetchTrades = async (requestedCoinId) => {
    if (!requestedCoinId) return;

    // Only show loading state if we don't have data yet
    if (trades.length === 0) {
      setLoading(true);
    }

    try {
      const response = await fetch(`/api/crypto/trades/${requestedCoinId}?limit=50`);
      const result = await response.json();

      // Discard response if the user has already switched to a different coin
      if (activeCoinRef.current !== requestedCoinId) return;

      if (result.success) {
        const formattedTrades = result.data.map(trade => ({
          id: trade.id,
          size: trade.quantity,
          price: trade.price,
          time: formatTime(trade.time),
          side: trade.isBuyerMaker ? 'sell' : 'buy'
        }));
        setTrades(formattedTrades);

        // Only report a price that is a valid positive finite number
        if (formattedTrades.length > 0 && onLastPriceUpdate) {
          const price = parseFloat(formattedTrades[0].price);
          if (isFinite(price) && price > 0) {
            onLastPriceUpdate(price);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="trade-history">
      <div className="trade-history-header">
        <h3>Trade History</h3>
        <div className="columns">
          <span>Trade Size</span>
          <span>Price (USD)</span>
          <span>Time</span>
        </div>
      </div>

      <div className="trades-list">
        {loading && trades.length === 0 ? (
          <div className="loading">Loading trades...</div>
        ) : trades.length > 0 ? (
          trades.map((trade) => (
            <div
              key={trade.id}
              className={`trade-row ${trade.side === 'buy' ? 'buy' : 'sell'}`}
            >
              <span className="size">{trade.size.toFixed(4)}</span>
              <span className="price">{formatPrice(trade.price)}</span>
              <span className="time">{trade.time}</span>
            </div>
          ))
        ) : (
          <div className="no-data">No trades available</div>
        )}
      </div>
    </div>
  );
}

export default TradeHistory;
